import React, { useEffect, useRef } from "react";
import { animate, prefersReducedMotion } from "../../lib/anime";

/**
 * The portrait resolved into a halftone grid — how a machine reads the photo.
 *
 * The canvas paints both the ground and the dots, so dissolving it uncovers the
 * real photo underneath cell by cell rather than cross-fading the whole plate.
 * Each cell carries its own delay, taken from its distance to where the pointer
 * entered, so the read comes apart as a ripple from under the cursor.
 *
 * The tween runs on a plain object and repaints imperatively — React re-renders
 * only when `dissolved` flips, never per frame.
 */

/* Share of the timeline spent staggering; the rest is each cell's own fade.
   The master clock is deliberately linear — the wave should cross the plate at
   a constant speed, and every cell already eases itself out with a smoothstep.
   Easing the master as well back-loads the whole dissolve into its last third. */
const SPREAD = 0.70;
const DURATION = 780;

const Halftone = ({
  src,
  alt,
  cell = 7,
  className = "",
  dissolved = false,
  origin,
  onReady,
}) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const inkRef = useRef(null);
  const groundRef = useRef(null);
  const api = useRef(null);
  const state = useRef({ t: 0 });
  const originRef = useRef(origin || { x: 0.5, y: 0.5 });

  if (origin) originRef.current = origin;

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext("2d", { alpha: true });
    let disposed = false;
    let sample = null;
    let delays = null;
    let cols = 0;
    let rows = 0;
    let frame = 0;

    /* Computed `color` is fully resolved; reading a custom property is not,
       which is why the two probe spans exist. Cached, because getComputedStyle
       forces a style recalc and this runs inside a 60fps repaint. */
    let ink = "#100F0D";
    let ground = "#DED7C7";
    const refreshColors = () => {
      if (inkRef.current) ink = getComputedStyle(inkRef.current).color || ink;
      if (groundRef.current) ground = getComputedStyle(groundRef.current).color || ground;
    };

    const buildSample = (image) => {
      const box = wrap.getBoundingClientRect();
      cols = Math.max(8, Math.floor(box.width / cell));
      rows = Math.max(8, Math.floor(box.height / cell));

      const off = document.createElement("canvas");
      off.width = cols;
      off.height = rows;
      const octx = off.getContext("2d");

      // cover-fit the source into the sample grid
      const ratio = Math.max(cols / image.width, rows / image.height);
      const w = image.width * ratio;
      const h = image.height * ratio;
      octx.drawImage(image, (cols - w) / 2, (rows - h) / 2, w, h);

      sample = octx.getImageData(0, 0, cols, rows).data;
      delays = null;
    };

    /* Per-cell delay, normalised 0..1 by the furthest corner from the origin. */
    const buildDelays = () => {
      const { x, y } = originRef.current;
      const ox = x * cols;
      const oy = y * rows;
      const far = Math.max(
        Math.hypot(ox, oy),
        Math.hypot(cols - ox, oy),
        Math.hypot(ox, rows - oy),
        Math.hypot(cols - ox, rows - oy)
      ) || 1;

      delays = new Float32Array(cols * rows);
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          delays[row * cols + col] = Math.hypot(col - ox, row - oy) / far;
        }
      }
    };

    /* Stops used to describe the wave as a gradient. Twelve is plenty for a
       monotonic ramp and costs nothing next to touching every cell. */
    const STOPS = 14;
    const TAU = Math.PI * 2;

    const draw = () => {
      if (disposed || !sample) return;
      if (!delays) buildDelays();

      const box = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.floor(box.width * dpr);
      const h = Math.floor(box.height * dpr);

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        canvas.style.width = `${box.width}px`;
        canvas.style.height = `${box.height}px`;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, box.width, box.height);

      const stepX = box.width / cols;
      const stepY = box.height / rows;
      const dotMax = Math.min(stepX, stepY) * 0.52;
      const t = state.current.t;

      /* Ground: one flat fill, then the wave erased out of it with a single
         radial gradient. Doing this per cell meant thousands of fillStyle
         assignments a frame — each one re-parsing a colour string — which cost
         about 200ms and dropped the dissolve to roughly 5fps. */
      ctx.globalAlpha = 1;
      ctx.fillStyle = ground;
      ctx.fillRect(0, 0, box.width, box.height);

      if (t > 0) {
        const ox = originRef.current.x * box.width;
        const oy = originRef.current.y * box.height;
        const farPx = Math.max(
          Math.hypot(ox, oy),
          Math.hypot(box.width - ox, oy),
          Math.hypot(ox, box.height - oy),
          Math.hypot(box.width - ox, box.height - oy)
        ) || 1;

        const wave = ctx.createRadialGradient(ox, oy, 0, ox, oy, farPx);
        for (let i = 0; i <= STOPS; i += 1) {
          const d = i / STOPS;
          let p = (t - d * SPREAD) / (1 - SPREAD);
          p = p < 0 ? 0 : p > 1 ? 1 : p;
          wave.addColorStop(d, `rgba(0,0,0,${p * p * (3 - 2 * p)})`);
        }

        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = wave;
        ctx.fillRect(0, 0, box.width, box.height);
        ctx.globalCompositeOperation = "source-over";
      }

      /* Dots: every arc collected into one path and filled once. They shrink to
         nothing on the same curve that erases the ground, so a dot never
         outlives the ground it was printed on. */
      ctx.fillStyle = ink;
      ctx.beginPath();

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const idx = row * cols + col;

          let p = (t - delays[idx] * SPREAD) / (1 - SPREAD);
          p = p < 0 ? 0 : p > 1 ? 1 : p;
          if (p >= 1) continue; // gone: the photo shows through here
          const left = 1 - p * p * (3 - 2 * p);

          const i = idx * 4;
          if (sample[i + 3] / 255 < 0.1) continue;

          // Rec. 601 luma — matches how the eye weighs the channels
          const luma =
            (sample[i] * 0.299 + sample[i + 1] * 0.587 + sample[i + 2] * 0.114) / 255;
          const radius = dotMax * Math.pow(1 - luma, 0.8) * left;
          if (radius < 0.2) continue;

          const cx = (col + 0.5) * stepX;
          const cy = (row + 0.5) * stepY;
          ctx.moveTo(cx + radius, cy);
          ctx.arc(cx, cy, radius, 0, TAU);
        }
      }

      ctx.fill();
    };

    api.current = {
      draw,
      refreshColors,
      invalidateDelays: () => {
        delays = null;
      },
    };

    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      if (disposed) return;
      buildSample(image);
      refreshColors();
      draw();
      onReady?.();
    };
    image.src = src;

    const onResize = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (image.complete && image.naturalWidth) buildSample(image);
        refreshColors();
        draw();
      });
    };

    window.addEventListener("resize", onResize);

    return () => {
      disposed = true;
      api.current = null;
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
    // onReady is a stable callback from the parent; re-running on it would
    // rebuild the sample for nothing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, cell]);

  /* The dissolve itself */
  useEffect(() => {
    const painter = api.current;
    if (!painter) return undefined;

    // The ripple starts wherever the pointer came in, and the ground colour may
    // have flipped with the polarity switch since the last paint.
    painter.invalidateDelays();
    painter.refreshColors();

    const target = dissolved ? 1 : 0;

    if (prefersReducedMotion()) {
      state.current.t = target;
      painter.draw();
      return undefined;
    }

    const tween = animate(state.current, {
      t: target,
      duration: DURATION,
      ease: "linear",
      onUpdate: painter.draw,
    });

    return () => tween.pause();
  }, [dissolved, origin]);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      {/* Colour probes: computed `color` resolves var(), getPropertyValue does not */}
      <span
        ref={inkRef}
        aria-hidden="true"
        style={{ position: "absolute", width: 0, height: 0, color: "var(--fg)" }}
      />
      <span
        ref={groundRef}
        aria-hidden="true"
        style={{ position: "absolute", width: 0, height: 0, color: "var(--bg)" }}
      />
      <canvas ref={canvasRef} className="h-full w-full" role="img" aria-label={alt} />
    </div>
  );
};

export default Halftone;
